import type { RecordRepositoryPort } from "@/application/records/ports/RecordRepositoryPort";
import { Record as RecordEntity } from "@/domain/records/entities/Record";
import type { AvailableVersions, RecordId } from "@/domain/records/types";
import { FileTypeDomainService } from "@/domain/upload/services/FileTypeDomainService";

/**
 * Record Repository implementation
 * Uses Insight API for data access
 */
export class RecordRepository implements RecordRepositoryPort {
  constructor(
    private readonly apiBaseUrl: string,
    private readonly insightApiUrl: string,
    private readonly authHeadersProvider?: () => Promise<Record<string, string>>
  ) {}

  async getById(id: RecordId): Promise<RecordEntity> {
    try {
      // Use optimized meta endpoint from Insight API
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(`${this.insightApiUrl}/insight/${id}/meta`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch record meta: ${response.statusText}`);
      }

      const data = await response.json();

      // Transform API response to Domain model
      return new RecordEntity(
        data.id,
        data.name,
        data.status,
        data.available_versions || {},
        data.size_mb || 0,
        data.file_extension,
        data.is_public || false,
        FileTypeDomainService.inferFileType(data.file_extension),
        data.created_at ? new Date(data.created_at) : undefined,
        data.updated_at ? new Date(data.updated_at) : undefined,
        data.participants_count
      );
    } catch (error) {
      console.error("RecordRepository.getById error:", error);
      throw new Error(
        `Failed to load record ${id}: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  async getByIds(ids: RecordId[]): Promise<RecordEntity[]> {
    try {
      // Use batch meta endpoint for multiple records
      const idsParam = ids.join(",");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(
        `${this.insightApiUrl}/insight/meta-batch?ids=${idsParam}`,
        {
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch records meta batch: ${response.statusText}`
        );
      }

      const data = await response.json();
      const records: RecordEntity[] = [];

      for (const id of ids) {
        const recordData = data.records?.[id];
        if (recordData) {
          records.push(
            new RecordEntity(
              recordData.id,
              recordData.name,
              recordData.status,
              recordData.available_versions || {},
              recordData.size_mb || 0,
              recordData.file_extension,
              recordData.is_public || false,
              FileTypeDomainService.inferFileType(recordData.file_extension),
              recordData.created_at
                ? new Date(recordData.created_at)
                : undefined,
              recordData.updated_at
                ? new Date(recordData.updated_at)
                : undefined,
              recordData.participants_count
            )
          );
        }
      }

      return records;
    } catch (error) {
      console.error("RecordRepository.getByIds error:", error);
      throw new Error(
        `Failed to load records: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  async exists(id: RecordId): Promise<boolean> {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(this.authHeadersProvider ? await this.authHeadersProvider() : {}),
      };

      const response = await fetch(`${this.insightApiUrl}/insight/${id}/meta`, {
        method: "HEAD",
        headers,
      });

      return response.ok;
    } catch (error) {
      console.error("RecordRepository.exists error:", error);
      return false;
    }
  }

  async getAvailableVersions(id: RecordId): Promise<AvailableVersions> {
    try {
      const record = await this.getById(id);
      return record.availableVersions;
    } catch (error) {
      console.error("RecordRepository.getAvailableVersions error:", error);
      return {};
    }
  }
}
